// @ts-strict-ignore
import { addMonths, format } from 'date-fns';
import isString from 'lodash/isString';
import { createSelector } from 'reselect';

import { DateFormat } from 'constants/date';
import { PlanType } from 'constants/plan-type';
import { PaymentType } from 'constants/product-cart';
import { secondsTimestampToDate } from 'helpers/date';
import { LCBillingCycleToProductCycleMap, getPlanPriceFromList } from 'helpers/subscription';
import { getIsOnePlanLevelDowngrade } from 'helpers/subscription-plans';
import { type Product } from 'interfaces/product-cart';
import type { CreditCardType } from 'interfaces/recurly';
import { BillingCycleType } from 'interfaces/subscription';
import {
  type WithAgentsState,
  getActiveAgentsCount,
  getLoggedInAgentLogin,
  getLoggedInAgentName,
} from 'store/entities/agents/selectors';
import {
  type IWithBillingEntitiesState,
  getBillingInfo,
  getHasApplicationsToUninstall,
  getIsSavingCreditCard,
} from 'store/entities/billing/selectors';
import {
  type IWithProductCartEntitiesState,
  getCurrentCartProductsToSubscribe,
  getIsProductCartFetching,
} from 'store/entities/product-cart/selectors';
import {
  type IWithRecurlyEntitiesState,
  getCouponRedemptionError,
  getIsLoadingCoupons,
  getNewRecurlyCoupon,
} from 'store/entities/recurly/selectors';
import { SubscriptionRequestFetchingSelectorsNames } from 'store/entities/subscription/actions';
import {
  type IWithSubscriptionState,
  getIsSubscriptionInTrial,
  getIsSubscriptionProcessing,
  getIsSubscriptionUnchanged,
  getPlanPrices,
  getPlans,
  getSubscription,
  getSubscriptionChange,
  getSubscriptionChangePlan,
  getSubscriptionChangePlanCode,
  getSubscriptionChangeSeats,
  getSubscriptionIsPastDue,
  getWasSubscriptionCreated,
} from 'store/entities/subscription/selectors';
import {
  type IWithAgentsSessionState,
  type IWithSessionState,
  getCanManageSubscription,
  getIsAfterStarterLimit,
  getIsPPABusinessModel,
  getIsSubscriptionExpired,
} from 'store/features/session/selectors';
import {
  type IWithRequestsState,
  createRequestErrorSelector,
  createRequestFetchingSelector,
} from 'store/requests/selectors';

import { getPlanPrice } from './helpers';
import type { CalendlyData, ISubscriptionViewState } from './interfaces';

export const CALENDLY_URL_FOR_TRIAL = 'https://calendly.com/d/ckt4-fb6-v6g';
export const CALENDLY_URL_FOR_PAID = 'https://calendly.com/text-product-experts/enterprise-discussion';

export interface IWithSubscriptionViewState {
  views: {
    subscription: ISubscriptionViewState;
  };
}

export function getSubscriptionError(state: IWithRequestsState): string {
  return (
    createRequestErrorSelector([SubscriptionRequestFetchingSelectorsNames.CREATE_SUBSCRIPTION_REQUEST])(state) ||
    createRequestErrorSelector([SubscriptionRequestFetchingSelectorsNames.UPDATE_SUBSCRIPTION_REQUEST])(state) ||
    createRequestErrorSelector([SubscriptionRequestFetchingSelectorsNames.UPGRADE_SUBSCRIPTION_REQUEST])(state)
  );
}

export function getIsAddingCardAndSubscription(state: IWithRequestsState): boolean {
  return getIsSavingCreditCard(state) || getIsSubscriptionProcessing(state);
}

export function getRecurlyError(state: IWithSubscriptionViewState): string | null {
  const recurlyError = state.views.subscription.recurlyError;

  if (isString(recurlyError) && recurlyError.includes('insufficient funds in your account')) {
    return 'Transaction declined due to insufficient funds. Use a different card or contact your bank';
  }

  return recurlyError;
}

export function getIsSubscriptionLoading(
  state: IWithRequestsState & IWithSubscriptionState & IWithSubscriptionViewState & IWithAgentsSessionState,
): boolean {
  const plans = getPlans(state);
  const arePlansFetched = !!plans && Object.keys(plans).length > 0;

  return (
    getCanManageSubscription(state) &&
    (!getSubscription(state) || !arePlansFetched || getIsProductCartFetching(state) || getIsLoadingCoupons(state)) &&
    !getIsAddingCardAndSubscription(state)
  );
}

export function getIsOwnerContactProcessing(state: IWithSubscriptionViewState & IWithRequestsState): boolean {
  return createRequestFetchingSelector([SubscriptionRequestFetchingSelectorsNames.CONTACT_SUBSCRIPTION_OWNER_REQUEST])(
    state,
  );
}

export function getIsLicenseDataRefreshFailure(state: IWithSubscriptionViewState): boolean {
  return state.views.subscription.licenseDataRefreshFailure;
}

export function getIsDowngradeConfirmFlowEnabled(state: IWithSubscriptionViewState): boolean {
  return state.views.subscription.isDowngradeConfirmFlowEnabled;
}

export function getCanRestartTrial(state: IWithSubscriptionViewState): boolean {
  return state.views.subscription.canRestartTrial;
}

export function getRecommendedPlan(state: IWithSubscriptionViewState): PlanType {
  return state.views.subscription.recommendedPlan;
}

export function getCompanySizeRecommendedPlan(state: IWithSubscriptionViewState): PlanType {
  return state.views.subscription.companySizeRecommendedPlan;
}

export function getHighlightedFeatures(state: IWithSubscriptionViewState): string[] {
  return state.views.subscription.highlightedFeatures || [];
}

export function getCurrentCardBrand(state: IWithSubscriptionViewState): CreditCardType {
  return state.views.subscription.currentCardBrand;
}

export function getIsSubscribeButtonDisabled(state: IWithSubscriptionViewState): boolean {
  return state.views.subscription.isSubscribeButtonDisabled;
}

export function getHasRecurlyAccount(state: IWithSubscriptionState & IWithBillingEntitiesState): boolean {
  const billingInfo = getBillingInfo(state);
  const subscription = getSubscription(state);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  return !!billingInfo || !!subscription?.card || !!subscription?.subscriber || subscription?.origin === 'recurly';
}

export function getIsNavigationVisible(state: IWithSubscriptionViewState): boolean {
  return state.views.subscription.showNavigation;
}

export function getShouldForcePPABusinessModel(state: IWithSessionState): boolean {
  const isPPABusinessModel = getIsPPABusinessModel(state);
  const isExpired = getIsSubscriptionExpired(state);

  return isPPABusinessModel || (!isPPABusinessModel && isExpired);
}

export function getCanApplyCovidDiscount(
  state: IWithSubscriptionViewState & IWithSubscriptionState & IWithRecurlyEntitiesState & IWithSessionState,
): boolean {
  const { plan: oldPlan } = getSubscription(state);
  const { billingCycle, plan: newPlan } = getSubscriptionChange(state);
  const newRecurlyCoupon = getNewRecurlyCoupon(state);
  const isOnePlanLevelDowngrade = getIsOnePlanLevelDowngrade(newPlan, oldPlan);
  const isPaidMonthly = billingCycle === BillingCycleType.Monthly;
  const newPlanIsNotStarter = newPlan !== PlanType.Starter;

  return isPaidMonthly && isOnePlanLevelDowngrade && newRecurlyCoupon.exists && newPlanIsNotStarter;
}

export const getIsSubscriptionChanged = createSelector(
  getSubscription,
  getIsSubscriptionUnchanged,
  (subscription, isSubscriptionUnchanged) =>
    subscription?.isExpired || !subscription?.subscriber || !isSubscriptionUnchanged,
);

export const getShouldAllowCheckoutStep = createSelector(
  getSubscription,
  getBillingInfo,
  (subscription, billingInfo) => subscription?.isPastDue || !billingInfo?.lastFour,
);

export const getIsNewCouponApplicable: (
  state: IWithSubscriptionViewState & IWithSubscriptionState & IWithRecurlyEntitiesState,
) => boolean = createSelector(
  getSubscriptionChangePlanCode,
  getNewRecurlyCoupon,
  (newSubscriptionPlanCode, newCoupon) =>
    newSubscriptionPlanCode &&
    newCoupon.exists &&
    !newCoupon.hideDiscount &&
    (newCoupon.appliesToAllPlans || newCoupon.plans.includes(newSubscriptionPlanCode)),
);

function getIsDownsellInfoVisible(
  state: IWithSubscriptionViewState & IWithSubscriptionState & IWithSessionState & WithAgentsState,
): boolean {
  const isPPABusinessModel = getShouldForcePPABusinessModel(state);
  const activeAgentsCount = getActiveAgentsCount(state);
  const newSubscriptionSeats = getSubscriptionChangeSeats(state);

  return isPPABusinessModel && activeAgentsCount > newSubscriptionSeats;
}

function getIsStarterUpsellInfoVisible(
  state: IWithSubscriptionViewState & IWithSubscriptionState & IWithSessionState & WithAgentsState,
): boolean {
  const isAfterStarterLimit = getIsAfterStarterLimit(state);
  const isExpired = getIsSubscriptionExpired(state);
  const newSubscriptionChangePlan = getSubscriptionChangePlan(state);
  const activeAgentsCount = getActiveAgentsCount(state);

  return (isExpired || isAfterStarterLimit) && activeAgentsCount > 1 && newSubscriptionChangePlan === PlanType.Starter;
}

/**
 * Checks if trial account was created through global billing system.
 * With global billing, subscription is created at account creation time.
 */
function getIsTrialWithRecurlyAccount(state: IWithSubscriptionState & IWithBillingEntitiesState): boolean {
  return getIsSubscriptionInTrial(state) && getHasRecurlyAccount(state);
}

export function getIsSubscriptionUpdateDisabled(
  state: IWithSubscriptionViewState &
    IWithSubscriptionState &
    IWithSessionState &
    WithAgentsState &
    IWithRecurlyEntitiesState &
    IWithBillingEntitiesState &
    IWithProductCartEntitiesState,
): boolean {
  const subscription = getSubscription(state);
  const isSubscriptionParamsUnchanged = getIsSubscriptionUnchanged(state);
  const isPastDue = getSubscriptionIsPastDue(state);
  const isExpired = getIsSubscriptionExpired(state);
  const isSubscriber = getWasSubscriptionCreated(state);
  const isDownsellInfoVisible = getIsDownsellInfoVisible(state);
  const isStarterUpsellInfoVisible = getIsStarterUpsellInfoVisible(state);
  const isNewCouponApplicable = getIsNewCouponApplicable(state);
  const areAppsAddedToCart = getCurrentCartProductsToSubscribe(state).length > 0;
  const hasApplicationsToUninstall = getHasApplicationsToUninstall(state);
  const couponRedemptionError = getCouponRedemptionError(state);
  const isUndoPendingChanges = getIsUndoPendingChanges(state);
  const isTrialWithRecurlyAccount = getIsTrialWithRecurlyAccount(state);
  const isSubscriptionUnchanged =
    isSubscriber && isSubscriptionParamsUnchanged && !hasApplicationsToUninstall && !areAppsAddedToCart;
  const hasPendingUpdate =
    subscription.pendingChange &&
    (subscription.pendingChange.seats !== subscription.seats ||
      subscription.pendingChange.plan !== subscription.plan ||
      subscription.pendingChange.billingCycle !== subscription.billingCycle);

  /*
   * Allow subscription updates for trial accounts with Recurly billing.
   * This exception is needed for global billing where subscription creation
   * happens at account creation time rather than at the end of trial
   */
  if (isTrialWithRecurlyAccount && isSubscriptionUnchanged) {
    return false;
  }

  return (
    (!isExpired &&
      isSubscriptionUnchanged &&
      !isUndoPendingChanges &&
      !isNewCouponApplicable &&
      !isPastDue &&
      !hasPendingUpdate) ||
    isDownsellInfoVisible ||
    isStarterUpsellInfoVisible ||
    !!couponRedemptionError
  );
}

/**
 * Map new subscription to IProduct interface.
 * To be used on manage view (cartItemCard and ReceiptItem).
 */
export const getNewSubscriptionAsProduct: (
  state: IWithSubscriptionViewState &
    IWithSubscriptionState &
    IWithSessionState &
    WithAgentsState &
    IWithBillingEntitiesState,
) => Product = createSelector(
  getSubscription,
  getSubscriptionChange,
  getIsSubscriptionInTrial,
  getPlanPrices,
  getIsSubscriptionExpired,
  getActiveAgentsCount,
  getBillingInfo,
  (subscription, newSubscription, inTrial, planPrices, isExpired, activeAgentsCount, billingInfo) => {
    // Calculate monthly price to see the difference between monthly and current subscription billing cycle
    // In case new billing cycle is monthly, there shouldn;t be any change
    // In case new billing cycle is annual we should show the difference in price and the discount
    const planPriceForChoosenBillingCycle = getPlanPrice(
      newSubscription,
      subscription,
      planPrices,
      newSubscription.billingCycle,
    );

    // Use original values to preserve price when plan/billing cycle/seats change
    const originalPlan = subscription?.plan || newSubscription.plan;
    const originalBillingCycle = subscription?.billingCycle || newSubscription.billingCycle;
    const originalSeats = (inTrial && !billingInfo ? activeAgentsCount : subscription?.seats) || 1;

    // Calculate prices using original values to prevent changes
    const originalPlanPrice = getPlanPriceFromList(originalPlan, BillingCycleType.Monthly, planPrices) as number;
    const originalPlanPriceForBillingCycle = getPlanPriceFromList(
      originalPlan,
      originalBillingCycle,
      planPrices,
    ) as number;

    const initialPrice = originalBillingCycle * originalPlanPrice;
    const regularPrice = initialPrice * originalSeats * 100; // Use original values to preserve price
    const totalPrice = originalPlanPriceForBillingCycle * originalBillingCycle * originalSeats * 100; // Use original values to preserve price
    const currentPriceWithActualSeats =
      planPriceForChoosenBillingCycle * newSubscription.billingCycle * newSubscription.seats * 100; // Use current values for potential future use
    const endDate = isExpired
      ? format(addMonths(Date.now(), subscription.billingCycle), DateFormat.TrialEndDate)
      : format(secondsTimestampToDate(subscription.currentPeriodEndsAt), DateFormat.TrialEndDate);

    const name = `${newSubscription.planName} plan`;

    return {
      id: 'livechat',
      name,
      plans: [
        {
          id: 'plan',
          billingCycle: LCBillingCycleToProductCycleMap[newSubscription.billingCycle], // Use new billing cycle for plan
          selected: true,
          type: PaymentType.PerAgent,
          price: {
            regular: regularPrice,
            discount: regularPrice - totalPrice,
            initial: initialPrice,
            total: totalPrice,
            currentPriceWithActualSeats, // Add current price with actual seats
          },
          discount: parseFloat((((regularPrice - totalPrice) / regularPrice) * 100).toFixed(2)),
          originalSeats, // Add original seats count for display
        },
      ],
      trialEndDate: inTrial && endDate,
      trialDays: 0,
      renewDate: !inTrial && endDate,
    };
  },
);

export const getIsCalendlyModalOpen = (state: IWithSubscriptionViewState): boolean =>
  state.views.subscription.isCalendlyModalOpen;

export const getIsComparePlansModalOpen = (state: IWithSubscriptionViewState): boolean =>
  state.views.subscription.isComparePlansModalOpen;

export const getShouldGoBackToPlansModal = (state: IWithSubscriptionViewState): boolean =>
  state.views.subscription.shouldGoBackToPlansModal;

export const getIsUndoPendingChanges = (state: IWithSubscriptionViewState): boolean =>
  state.views.subscription.isUndoPendingChanges;

export const getCalendlyData = (
  state: IWithSubscriptionViewState & IWithSubscriptionState & WithAgentsState,
): CalendlyData => {
  const agentLogin = getLoggedInAgentLogin(state);
  const agentName = getLoggedInAgentName(state);
  const isPaid = getSubscription(state)?.subscriber;

  const calendlyUrl = isPaid ? CALENDLY_URL_FOR_PAID : CALENDLY_URL_FOR_TRIAL;

  return {
    url: calendlyUrl,
    prefill: {
      email: agentLogin,
      name: agentName,
    },
  };
};
